import { cleDeComparaison } from './foldLigatures';
import type { RecipeCourse, Unit } from './schema';

/**
 * Couche de présentation (US-16). Les unités canoniques du §3.2 CLAUDE.md
 * (`piece`, `pincee`, `gousse`…) sont des identifiants de stockage, choisis
 * pour être stables et sans accent — pas du français. Jusqu'ici elles étaient
 * rendues telles quelles, y compris sur l'écran le plus regardé : le mode
 * cuisine affichait « 1 piece oignon » et « 1 piece pomme de terre ». Même
 * chose pour les durées, données en minutes brutes (« 150 min cuisson » pour
 * le bœuf bourguignon).
 *
 * Ce module est la frontière : la base et l'API continuent de parler en
 * unités canoniques et en minutes, l'écran parle français. **Aucune donnée
 * n'est transformée en écriture** — c'est de l'affichage seulement.
 *
 * Placé dans `shared/` et non dans `client/` parce que l'export PDF
 * (`server/src/export/buildRecipePdf.ts`) doit produire exactement le même
 * texte que l'écran.
 *
 * L'arrondi et les fractions des quantités ne sont pas ici : ils existent
 * déjà, testés, dans `server/src/ingredients/adaptQuantities.ts`
 * (`formatQuantityForDisplay`, §3.1). Ce module assemble, il ne recalcule pas.
 */

// ----------------------------------------------------------------------------
// Unités
// ----------------------------------------------------------------------------

/**
 * Forme affichée de chaque unité canonique, au singulier et au pluriel.
 *
 * `piece` est volontairement vide dans les deux cas : le nom de l'ingrédient
 * porte déjà le nombre (§2 : `name` est « tel qu'écrit dans la recette »,
 * donc « 2 oignons » a été analysé en quantity=2, name='oignons'). Ajouter
 * un mot produirait « 2 pièces oignons ».
 *
 * Les symboles de masse et de volume sont invariables, comme le veut l'usage
 * typographique français des symboles d'unité. Les abréviations anglo-saxonnes
 * le sont aussi, à l'exception de `cup`, qui s'écrit couramment « cups ».
 */
const UNIT_DISPLAY: Record<Unit, { one: string; many: string }> = {
  g: { one: 'g', many: 'g' },
  kg: { one: 'kg', many: 'kg' },

  ml: { one: 'ml', many: 'ml' },
  cl: { one: 'cl', many: 'cl' },
  l: { one: 'l', many: 'l' },
  'c. à c.': { one: 'c. à c.', many: 'c. à c.' },
  'c. à s.': { one: 'c. à s.', many: 'c. à s.' },

  piece: { one: '', many: '' },
  pincee: { one: 'pincée', many: 'pincées' },
  botte: { one: 'botte', many: 'bottes' },
  gousse: { one: 'gousse', many: 'gousses' },
  brin: { one: 'brin', many: 'brins' },
  tranche: { one: 'tranche', many: 'tranches' },

  // Domestiques (US-26). « noix » est invariable au pluriel.
  pot: { one: 'pot', many: 'pots' },
  sachet: { one: 'sachet', many: 'sachets' },
  verre: { one: 'verre', many: 'verres' },
  cuillere: { one: 'cuillère', many: 'cuillères' },
  bol: { one: 'bol', many: 'bols' },
  noix: { one: 'noix', many: 'noix' },

  cup: { one: 'cup', many: 'cups' },
  oz: { one: 'oz', many: 'oz' },
  lb: { one: 'lb', many: 'lb' },
  tsp: { one: 'tsp', many: 'tsp' },
  tbsp: { one: 'tbsp', many: 'tbsp' },
};

/** Le français n'accorde qu'à partir de deux : « 1,5 pincée », « 2 pincées ». */
function isPlural(quantity: number | null): boolean {
  return quantity !== null && quantity >= 2;
}

/** Forme affichable d'une unité, accordée. Chaîne vide s'il n'y a rien à écrire. */
export function formatUnit(unit: Unit | null, quantity: number | null): string {
  if (unit === null) return '';
  const forms = UNIT_DISPLAY[unit];
  return isPlural(quantity) ? forms.many : forms.one;
}

/**
 * Assemble ce qui précède le nom de l'ingrédient : « 800 g », « 3 gousses »,
 * « 1 » pour une pièce. `quantityDisplay` vient de `formatQuantityForDisplay`
 * (§3.1) — déjà arrondi, déjà en fractions ; `quantity` est la valeur
 * numérique correspondante, nécessaire pour accorder l'unité.
 *
 * L'unité est formatée ici plutôt que reçue déjà formatée : un appelant qui
 * passerait l'unité canonique brute réintroduirait sans s'en rendre compte le
 * défaut que ce module existe pour corriger.
 *
 * Renvoie une chaîne vide quand il n'y a pas de quantité : « Sel, poivre »
 * s'affiche seul (§3.2, règle de repli).
 */
export function joinQuantityUnit(
  quantityDisplay: string | null,
  unit: Unit | null,
  quantity: number | null,
): string {
  if (quantityDisplay === null || quantityDisplay === '') return '';
  const unitDisplay = formatUnit(unit, quantity);
  if (unitDisplay === '') return quantityDisplay;
  return `${quantityDisplay} ${unitDisplay}`;
}

// ----------------------------------------------------------------------------
// Durées
// ----------------------------------------------------------------------------

/**
 * Durée lisible. Sous une heure en minutes, au-delà en heures et minutes :
 * « 2 h 30 » se lit d'un coup d'œil, « 150 min » demande un calcul.
 *
 * `0` est traité comme « pas de durée » et non comme « zéro minute » : la
 * base stocke `null` pour une durée absente, mais un import peut produire `0`,
 * et « 0 min de cuisson » n'a aucun sens à l'écran.
 */
export function formatDuration(minutes: number | null): string | null {
  if (minutes === null || minutes <= 0) return null;
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} h` : `${hours} h ${rest}`;
}

/**
 * Durée d'**étape**, lisible, à partir de secondes (US-98).
 *
 * Une fonction distincte de `formatDuration` plutôt qu'un paramètre d'unité :
 * les deux unités coexistent durablement — les temps de la recette
 * (`prep_min`, `rest_min`, `cook_min`) restent en minutes, personne n'ayant
 * jamais eu besoin d'écrire « préparation : 90 secondes » —, et un booléen
 * `enSecondes` posé au mauvais endroit produirait un facteur soixante muet.
 * Deux noms, deux unités, rien à vérifier à la lecture d'un appel.
 *
 * | secondes | affichage |
 * | --- | --- |
 * | 20 | « 20 sec » |
 * | 90 | « 1 min 30 » |
 * | 120 | « 2 min » |
 * | 5 400 | « 1 h 30 » |
 *
 * Les secondes ne s'affichent **que sous la minute, et en appoint d'une
 * minute** : au-delà de l'heure, « 1 h 30 min 20 » est une précision que
 * personne ne surveille, et elle est écartée.
 */
export function formatDureeSecondes(secondes: number | null): string | null {
  if (secondes === null || secondes <= 0) return null;
  if (secondes < 60) return `${secondes} sec`;
  const minutesEntieres = Math.floor(secondes / 60);
  const resteSecondes = secondes % 60;
  if (minutesEntieres < 60) {
    return resteSecondes === 0 ? `${minutesEntieres} min` : `${minutesEntieres} min ${resteSecondes}`;
  }
  return formatDuration(minutesEntieres);
}

export interface DurationParts {
  prep_min: number | null;
  rest_min: number | null;
  cook_min: number | null;
}

export interface TotalDuration {
  /** Somme en minutes, repos compris. */
  total: number;
  /** Total lisible (« 3 h 10 »). */
  label: string;
  /** Temps où le cuisinier est occupé (« 40 min ») — le repos n'en fait pas partie. */
  activeLabel: string | null;
}

/**
 * Temps total et temps actif. Le temps total est le premier critère de choix
 * d'une recette un soir de semaine ; il n'était affiché nulle part, chaque
 * écran listant préparation et cuisson côte à côte sans les additionner.
 *
 * Le repos compte dans le total (il faut bien l'attendre) mais pas dans le
 * temps actif : une pâte qui lève douze heures n'occupe personne. C'est la
 * distinction que porte déjà `step.is_passive` (§2).
 */
export function formatTotalDuration(parts: DurationParts): TotalDuration | null {
  const values = [parts.prep_min, parts.rest_min, parts.cook_min].map((v) => (v === null || v <= 0 ? 0 : v));
  const total = values.reduce((sum, v) => sum + v, 0);
  if (total === 0) return null;
  const label = formatDuration(total);
  if (label === null) return null;
  return { total, label, activeLabel: formatDuration(parts.prep_min) };
}

// ----------------------------------------------------------------------------
// Portions
// ----------------------------------------------------------------------------

/**
 * Accord du libellé de portions. `servings_label` est un champ libre (§2) :
 * « personnes », « parts », mais aussi « cookies » ou « crêpes ». On accorde
 * ce qu'on connaît, et pour le reste on se contente de retirer un « s » final
 * au singulier — jamais d'en ajouter un, ce qui inventerait des pluriels faux
 * sur un champ que l'utilisateur remplit librement.
 */
export function formatServings(count: number | null, label: string | null): string | null {
  if (count === null) return null;
  const raw = label === null || label.trim() === '' ? 'portions' : label.trim();
  if (isPlural(count)) return `${count} ${raw}`;
  const singular = raw.endsWith('s') && raw.length > 1 ? raw.slice(0, -1) : raw;
  return `${count} ${singular}`;
}

// ----------------------------------------------------------------------------
// Difficulté et dates
// ----------------------------------------------------------------------------

/**
 * Difficulté en toutes lettres. Elle était codée par trois pastilles de 6 px
 * sans légende visible (audit externe, constat 23) : l'`aria-label` disait
 * « Difficulté 1 sur 3 », l'œil ne disait rien. Une échelle à trois niveaux
 * mérite un mot, pas un chiffrement.
 */
export function formatDifficulty(difficulty: number | null): string | null {
  if (difficulty === 1) return 'Facile';
  if (difficulty === 2) return 'Moyen';
  if (difficulty === 3) return 'Difficile';
  return null;
}

/**
 * Date relative courte (« il y a 3 semaines »), pensée pour tenir sur une
 * carte. `now` est injectable pour que les tests ne dépendent pas de l'heure
 * courante — c'est la raison pour laquelle cette fonction est ici plutôt que
 * dupliquée entre `RecipeList` et la fiche, comme elle l'était.
 *
 * Pas d'`Intl.RelativeTimeFormat` : les seuils voulus (jour, semaine, mois,
 * année, arrondis) s'expriment plus simplement à la main qu'à travers son API
 * de style « auto ».
 */
export function formatRelativeDate(iso: string, now: number): string {
  const days = Math.max(0, Math.round((now - new Date(iso).getTime()) / 86_400_000));
  if (days === 0) return 'aujourd’hui';
  if (days === 1) return 'hier';
  if (days < 7) return `il y a ${days} jours`;
  if (days < 30) {
    const weeks = Math.round(days / 7);
    return `il y a ${weeks} semaine${weeks > 1 ? 's' : ''}`;
  }
  if (days < 365) {
    const months = Math.round(days / 30);
    return `il y a ${months} mois`;
  }
  const years = Math.round(days / 365);
  return `il y a ${years} an${years > 1 ? 's' : ''}`;
}

/**
 * Échelle française des thermostats : la position vaut trente degrés (US-38).
 * Même convention que la lecture inverse d'`extractTemperature`, écrite une
 * seule fois de chaque côté plutôt que devinée deux fois.
 */
const DEGRES_PAR_THERMOSTAT = 30;

/**
 * Température de four, en degrés et — quand la conversion tombe juste — en
 * position de thermostat : « 180 °C (th. 6) ».
 *
 * **La position n'est affichée que si la division est exacte.** « 170 °C »
 * vaudrait 5,67 : annoncer « th. 6 » serait faux de dix degrés, et le §3.2
 * interdit d'arrondir une valeur qu'on n'a pas mesurée. Mieux vaut ne rien dire
 * que dire à peu près — un four réglé une position trop haut brûle un gâteau.
 *
 * Bornée à l'échelle réelle, de 1 à 10 : au-delà, la position n'existe sur
 * aucun four domestique et n'apprendrait rien.
 */
export function formatOvenTemperature(celsius: number | null): string | null {
  if (celsius === null || !Number.isFinite(celsius)) return null;
  const degres = `${String(celsius)} °C`;
  if (celsius % DEGRES_PAR_THERMOSTAT !== 0) return degres;
  const position = celsius / DEGRES_PAR_THERMOSTAT;
  if (position < 1 || position > 10) return degres;
  return `${degres} (th. ${String(position)})`;
}

/**
 * Libellé d'une nature de plat (US-55).
 *
 * `course` est une valeur d'énumération : elle ne s'affiche **jamais** brute.
 * C'est la règle du §3.5, née de « 1 piece oignon » et de
 * `linear / none / sublinear` visibles en production.
 */
export function formatCourse(course: RecipeCourse | null): string | null {
  if (course === null) return null;
  const libelles: Record<RecipeCourse, string> = {
    entree: 'Entrée',
    plat: 'Plat',
    dessert: 'Dessert',
    accompagnement: 'Accompagnement',
    boisson: 'Boisson',
    // Ce qui n'est pas un plat mais un composant : pâte feuilletée, crème
    // pâtissière, sauce. Une part réelle du corpus, qui n'avait nulle part où
    // aller avant cette story.
    base: 'Préparation de base',
  };
  return libelles[course];
}

/**
 * Libellé d'une cuisine (US-55). Champ libre : on n'affiche que ce qui a été
 * saisi, avec une majuscule initiale — « italienne » et « Italienne » sont la
 * même cuisine, autant qu'elles se présentent pareil.
 */
export function formatCuisine(cuisine: string | null): string | null {
  const propre = cuisine?.trim() ?? '';
  if (propre === '') return null;
  return propre.charAt(0).toUpperCase() + propre.slice(1);
}

/**
 * D'où vient une recette (US-74).
 *
 * ### Pourquoi c'est ici et pas dans le client
 *
 * La marque de provenance est une affaire d'écran ; « le procédé est dans une
 * vidéo » ne l'est pas. C'est un **critère d'étiquette calculée**
 * (`source_video`, `server/src/tags/rules.ts`), donc du code serveur, et une
 * seconde table de sources vivant dans `client/` aurait divergé de celle-ci au
 * premier ajout — une recette porterait alors la marque sans l'étiquette, ou
 * l'inverse. Une table, un seul endroit qui décide.
 *
 * ### Ce que la table contient, et ce qu'elle ne contient pas
 *
 * **Instagram, et rien d'autre pour l'instant.** C'est la seule source dont
 * cette vague a mesuré le comportement (§C4 du plan) : la légende donne les
 * ingrédients, le procédé reste dans la vidéo. TikTok et YouTube sont
 * plausibles, non vérifiés, et hors vague G — les ajouter ici sur une
 * intuition poserait « procédé en vidéo » sur des recettes dont personne n'a
 * regardé la source. Le jour où l'un d'eux entre vraiment, **c'est une ligne**,
 * et le glyphe correspondant en est une autre dans `client/src/ui/SourceMark`.
 *
 * ### Comment on reconnaît une source
 *
 * `source_name` d'abord — c'est ce que le raccourci d'US-72 écrit
 * (« Instagram »), et c'est ce que le plan désigne comme pilote de la marque.
 * L'hôte de `source_url` ensuite, pour la recette collée à la main dont
 * personne n'a rempli la source : l'hôte est une **donnée de l'adresse**, pas
 * une devinette sur le contenu. Un titre ou un texte qui « parle
 * d'Instagram » ne compte jamais — ce serait deviner, ce que le §3 proscrit.
 */
export interface Provenance {
  /** Identifiant stable, jamais affiché. Sert à choisir le glyphe. */
  cle: 'instagram';
  /** Nom affichable — « Vient d'Instagram », « Revoir sur Instagram ». */
  nom: string;
  /** Le procédé d'une recette de cette source est dans une vidéo, pas dans le texte. */
  video: boolean;
}

interface SourceConnue extends Provenance {
  /** Formes de `source_name` qui désignent cette source, comparées repliées. */
  noms: readonly string[];
  /** Hôtes qui désignent cette source. Comparés sur le suffixe, `www.` compris. */
  hotes: readonly string[];
}

const SOURCES_CONNUES: readonly SourceConnue[] = [
  {
    cle: 'instagram',
    nom: 'Instagram',
    video: true,
    noms: ['instagram'],
    hotes: ['instagram.com', 'instagr.am'],
  },
];

/** Hôte d'une adresse, ou `null` si elle n'en est pas une. Ne lève jamais. */
function hoteDe(url: string | null): string | null {
  if (url === null || url.trim() === '') return null;
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return null;
  }
}

export function provenanceDeRecette(
  recette: { source_name: string | null; source_url: string | null },
): Provenance | null {
  const nom = cleDeComparaison(recette.source_name);
  const hote = hoteDe(recette.source_url);
  const trouvee = SOURCES_CONNUES.find(
    (source) =>
      (nom !== '' && source.noms.includes(nom)) ||
      (hote !== null && source.hotes.some((connu) => hote === connu || hote.endsWith(`.${connu}`))),
  );
  if (trouvee === undefined) return null;
  return { cle: trouvee.cle, nom: trouvee.nom, video: trouvee.video };
}
